import { ErrorPayload } from 'vite'

import pageReloadService from '@/lib/services/pageReloadService'

export function registerGlobalEventListeners() {
  handleRuntimeError()
  handleVitePreloadError()
  pageReloadService.trackProgrammaticReload()
  disableNumberInputs()
}

// Show an error overlay when a runtime error occurs (only applies on dev)
function handleRuntimeError() {
  window.addEventListener('error', ({ error }) => showErrorOverlay(error))
  window.addEventListener('unhandledrejection', ({ reason }) =>
    showErrorOverlay(reason),
  )

  /**
   * Vite's overlay is built for an `ErrorPayload['err']` off the dev server,
   * but what reaches these listeners is whatever was thrown — which needn't be
   * an Error, or even a value. Code is free to throw a string or reject with
   * nothing, and the browser reports some failures it won't disclose (a
   * cross-origin script, or the benign "ResizeObserver loop completed with
   * undelivered notifications") as an error event with a null `error`.
   *
   * The overlay reads `err.frame` before anything else, so any of those threw a
   * TypeError out of its constructor — and that TypeError then stood in the
   * console where the original error should have been. A broken overlay is a
   * nuisance; one that hides the error it exists to show is worse than none,
   * which is what the normalising and the try/catch below are for.
   */
  function showErrorOverlay(error: unknown) {
    const ErrorOverlay = customElements.get('vite-error-overlay')

    if (!ErrorOverlay) {
      return
    }

    // No payload means the browser is declining to say what went wrong, so
    // there's nothing to show and nothing worth logging either — a bare `null`
    // in the console tells nobody anything.
    if (error === null || error === undefined) {
      return
    }

    console.error(error)

    try {
      document.body.appendChild(new ErrorOverlay(toErrorPayload(error)))
    } catch (overlayError) {
      console.error('Failed to render the Vite error overlay', overlayError)
    }
  }
}

// The overlay only reads `message` and `stack` off a runtime error; the rest of
// the payload describes a build-time failure and has no equivalent here.
function toErrorPayload(error: unknown): ErrorPayload['err'] {
  return error instanceof Error
    ? { message: error.message, stack: error.stack ?? '' }
    : { message: String(error), stack: '' }
}

// If we fail to load a dynamic import, reload the page
// https://vitejs.dev/guide/build#load-error-handling
function handleVitePreloadError() {
  window.addEventListener('vite:preloadError', () => {
    console.log('vite:preloadError occurred.')

    if (pageReloadService.isProgrammaticReload()) {
      console.log(
        'Already reloaded page programmatically. Not reloading again as this ' +
          'might cause infinite reloads.',
      )
      return
    }

    console.log('Reloading the page...')
    pageReloadService.reloadPage()
  })
}

// Disable scrolling on input[type="number"] elements
// https://stackoverflow.com/questions/9712295/disable-scrolling-on-input-type-number
function disableNumberInputs() {
  document.addEventListener('wheel', function () {
    const activeElement = document.activeElement

    const isNumberInput =
      activeElement instanceof HTMLInputElement &&
      activeElement.type === 'number'

    if (isNumberInput) {
      activeElement.blur()
    }
  })
}
